import React, { useState } from 'react';
import { string, bool } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import { Form, PrimaryButton, FieldTextInput, IconInquiry, Heading } from '../../../components';

import css from './CustomRequestForm.module.css';
import { useConfiguration } from '../../../context/configurationContext';

const ErrorMessage = props => {
  const { error } = props;
  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="CustomRequestForm.customQuoteRequestErrorNoProcess" />
      ) : error.status === 403 ? (
        <FormattedMessage id="CustomRequestForm.loginError" />
      ) : (
        <FormattedMessage id="CustomRequestForm.customQuoteRequestError" />
      )}
    </p>
  ) : null;
};

// NOTE: this CustomRequestForm is only for booking & purchase processes
// The default-inquiry process is handled differently
const CustomRequestFormCoponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress,
        intl,
        listingTitle,
        authorDisplayName,
        customQuoteRequestError,
      } = fieldRenderProps;

      const [showError, setShowError] = useState(false);
      const [showErrorMessage, setShowErrorMessage] = useState(null);

      const messageLabel = intl.formatMessage(
        {
          id: 'CustomRequestForm.messageLabel',
        },
        { authorDisplayName }
      );
      const messagePlaceholder = intl.formatMessage(
        {
          id: 'CustomRequestForm.messagePlaceholder',
        },
        { authorDisplayName }
      );
      const messageRequiredMessage = intl.formatMessage({
        id: 'CustomRequestForm.messageRequired',
      });
      const messageRequired = validators.enquiryFormMessageValidator(messageRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = submitInProgress;

      const config = useConfiguration();

      const marketplaceRegexPatterns = config.listing.marketplaceRegexPatterns;

      const invalidKeywordMsg = intl.formatMessage({
        id: 'SendMessageForm.invalidKeyWord',
      });

      const handleKeyPress = e => {
        const inputValue = e.target.value;
        const regexPattern = marketplaceRegexPatterns;

        if (regexPattern.test(inputValue)) {
          setShowErrorMessage(invalidKeywordMsg);
          setShowError(true);
        } else if (!regexPattern.test(inputValue)) {
          setShowErrorMessage(null);
          setShowError(false);
        }
      };

      return (
        <Form
          className={classes}
          onSubmit={e => {
            e.preventDefault();

            !submitDisabled && handleSubmit(e);
          }}
          enforcePagePreloadFor="OrderDetailsPage"
        >
          <IconInquiry className={css.icon} />
          <Heading as="h2" rootClassName={css.heading}>
            <FormattedMessage id="CustomRequestForm.heading" values={{ listingTitle }} />
          </Heading>
          <FieldTextInput
            className={css.field}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
            validate={validators.requiredAndNonEmptyString(messageRequiredMessage)}
            onKeyUp={handleKeyPress}
            onKeyDown={handleKeyPress}
          />
          {showError && <p className={css.error}>{showErrorMessage}</p>}
          <div className={submitButtonWrapperClassName}>
            <ErrorMessage error={customQuoteRequestError} />
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="CustomRequestForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

CustomRequestFormCoponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  customQuoteRequestError: null,
};

CustomRequestFormCoponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  inProgress: bool,

  listingTitle: string.isRequired,
  authorDisplayName: string.isRequired,
  customQuoteRequestError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const CustomRequestForm = compose(injectIntl)(CustomRequestFormCoponent);

CustomRequestForm.displayName = 'CustomRequestForm';

export default CustomRequestForm;
